import React, { useEffect, useState } from "react";
import { Container, Button, Modal, Spinner } from "react-bootstrap";
import { FaEye, FaRedo } from "react-icons/fa";
import { GridColDef } from "@mui/x-data-grid";
import EmailLogTable from "../components/EmailLogTable";
import ViewBodyModal from "../components/ViewBodyModal";
import { getListaEmailLog, reenviarEmailLog } from "../../../services/emailLogService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import { getPageName, getUiInterface } from "../../../locales/translationUtils";
import { useTranslation } from "react-i18next";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { ApiResponse } from "../../../services/api";
import EmailLogModel from "../../../types/emailLog";

const EmailLogListView: React.FC = () => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<EmailLogModel[]>([]);
  const [apiResponse, setApiResponse] = useState<ApiResponse<EmailLogModel> | null>(null);
  const [exportando, setExportando] = useState(false);
  const [emailSelecionado, setEmailSelecionado] = useState<EmailLogModel | null>(null);

  // Reenvio: disponivel pra qualquer e-mail, sucesso ou falha - o destinatario pode
  // simplesmente nao ter recebido mesmo com o envio original tendo dado certo.
  const [emailParaReenviar, setEmailParaReenviar] = useState<EmailLogModel | null>(null);
  const [reenviando, setReenviando] = useState(false);

  // Desligado por padrao: mostra tudo, o suporte liga quando quer ir direto ao problema.
  const [somenteComErro, setSomenteComErro] = useState(false);

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 40,
    page: 0,
  });

  const buscar = async () => {
    const resposta = await getListaEmailLog({
      somenteComErro,
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString(),
    });
    setApiResponse(resposta);
    setEmails(resposta?.data?.values ?? []);
  };

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, somenteComErro]);

  const handleExportarExcel = async () => {
    setExportando(true);

    try {
      const resposta = await getListaEmailLog({
        somenteComErro,
        pageNum: '0',
        quantityPerPage: '100000',
      });

      const linhas = (resposta?.data?.values ?? []).map((row) => ({
        [t('emailLog.fields.enviadoEm')]: new Date(row.enviadoEm).toLocaleString(),
        [t('emailLog.fields.origem')]: row.origem,
        [t('emailLog.fields.destinatarios')]: row.destinatarios,
        [t('emailLog.fields.assunto')]: row.assunto,
        [t('emailLog.fields.sucesso')]: row.sucesso ? getUiInterface('success') : getUiInterface('failure'),
        [t('emailLog.fields.mensagemErro')]: row.mensagemErro ?? '',
      }));

      await exportarParaExcel(linhas, 'emails_enviados');
    } finally {
      setExportando(false);
    }
  };

  const handleReenviar = async () => {
    if (!emailParaReenviar) return;

    setReenviando(true);
    try {
      await reenviarEmailLog(emailParaReenviar.id);
      window.alert(t('emailLog.labels.resendSuccess'));
      setEmailParaReenviar(null);
      await buscar();
    } catch (error: any) {
      const backendMessage = error?.response?.data?.messages?.[0];
      window.alert(backendMessage || t('emailLog.labels.resendError'));
    } finally {
      setReenviando(false);
    }
  };

  const navItems: NavBarItem[] = [
    { label: t('emailLog.labels.home'), route: "/", iconName: "house-door" },
    {
      label: t('emailLog.labels.somenteComErro'),
      iconName: "exclamation-triangle",
      type: "toggle",
      checked: somenteComErro,
      onToggle: setSomenteComErro,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "enviadoEm",
      headerName: t('emailLog.fields.enviadoEm'),
      width: 160,
      valueFormatter: (value: any) => value ? new Date(value).toLocaleString() : '-',
    },
    { field: "origem", headerName: t('emailLog.fields.origem'), width: 140 },
    { field: "destinatarios", headerName: t('emailLog.fields.destinatarios'), width: 280 },
    { field: "assunto", headerName: t('emailLog.fields.assunto'), width: 320 },
    {
      field: "sucesso",
      headerName: t('emailLog.fields.sucesso'),
      width: 110,
      renderCell: (params) => (
        <span className={params.row.sucesso ? "text-success" : "text-danger"}>
          <i className={`bi bi-${params.row.sucesso ? 'check-circle-fill' : 'x-circle-fill'} me-1`}></i>
          {params.row.sucesso ? getUiInterface('success') : getUiInterface('failure')}
        </span>
      ),
    },
    {
      field: "mensagemErro",
      headerName: t('emailLog.fields.mensagemErro'),
      width: 260,
      renderCell: (params) => params.row.mensagemErro || '-',
    },
    {
      field: "ver",
      headerName: '',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Button
            variant="link"
            size="sm"
            onClick={() => setEmailSelecionado(params.row)}
            title={t('emailLog.labels.viewBody')}
          >
            <FaEye />
          </Button>
          {/* Sempre ativo, mesmo com sucesso=true: o destinatario pode nao ter recebido
              mesmo com o envio original tendo dado certo. */}
          <Button
            variant="link"
            size="sm"
            onClick={() => setEmailParaReenviar(params.row)}
            title={t('emailLog.labels.resend')}
          >
            <FaRedo />
          </Button>
        </>
      ),
    },
  ];

  return (
    <Container
      fluid
      style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}
    >
      <NavBar items={navItems} />
      <div className="mb-2"></div>
      <h2>{getPageName("emailLog")}</h2>
      <div className="mb-2"></div>

      <div className="d-flex justify-content-end mb-2">
        <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <EmailLogTable
          rows={emails}
          columns={columns}
          apiResponse={apiResponse}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
        />
      </div>

      <ViewBodyModal email={emailSelecionado} onClose={() => setEmailSelecionado(null)} />

      <Modal show={!!emailParaReenviar} onHide={() => setEmailParaReenviar(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('emailLog.labels.confirmResend')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('emailLog.labels.confirmResendMessage')}</p>
          {emailParaReenviar && (
            <p className="text-muted mb-0">
              <strong>{t('emailLog.fields.destinatarios')}:</strong> {emailParaReenviar.destinatarios}<br />
              <strong>{t('emailLog.fields.assunto')}:</strong> {emailParaReenviar.assunto}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEmailParaReenviar(null)} disabled={reenviando}>
            {getUiInterface('no')}
          </Button>
          <Button variant="primary" onClick={handleReenviar} disabled={reenviando}>
            {reenviando ? <Spinner animation="border" size="sm" /> : getUiInterface('yes')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EmailLogListView;
